import Image from 'next/image';

import { motion } from 'framer-motion';
import { uppercaseFirstLetter } from '@/utils/string';
import MenuPolygon from '@/assets/decorative/menuPolygon.svg';
import { Section, SubSection } from './useGcssSections';
import { mergeClasses } from '@/utils/style';
import { Link } from '@/components/links';
import { Button } from '@/components/buttons';
import { getMediaQuery } from '@/styles/sizes';
import useMediaQuery from '@/hooks/useMediaQuery';
import { useRouter } from 'next/router';
import { navLinks } from '@/sections/layout/Header/Header';

import { useTranslation } from 'react-i18next';
import CrossIcon from '@/assets/icons/heroIcons/Cross.svg';
import {
	DynamicDirectionIndicators,
	getSubmenuScrollOffset,
	VariantsOptions,
	variants,
} from './Subsection';

const polygonVariants = {
	open: ({ isScrolling, isResponsive, isLargeDesktop }: VariantsOptions) => ({
		zIndex: 10,
		opacity: 1,
		y: 45 + (isScrolling ? getSubmenuScrollOffset(isLargeDesktop) : 0),
		display: isResponsive ? 'block' : 'none',
		transition: {
			delay: 0.2,
		},
	}),
	closed: ({ isScrolling, isLargeDesktop }: VariantsOptions) => ({
		zIndex: 0,
		opacity: 0,
		y: 80 + (isScrolling ? getSubmenuScrollOffset(isLargeDesktop) : 0),
		display: 'none',
		transition: {
			delay: -0.2,
		},
	}),
};

const itemVariants = {
	open: {
		opacity: 1,
		transition: {
			y: { stiffness: 500, velocity: -50 },
		},
	},
	closed: {
		opacity: 0,
		transition: {
			y: { stiffness: 500 },
		},
	},
};

function isHighlighted(sectionId: Section['id']) {
	return sectionId === 'allProducts';
}

export function GcssSubsection({
	subsection,
	sectionId,
	sectionName,
	isOpen,
	handleClick,
	isScrolling,
	isSectionActive,
	variant,
}: {
	subsection: SubSection[];
	sectionId: Section['id'];
	sectionName: Section['name'];
	isOpen: boolean;
	handleClick: () => void;
	isScrolling: boolean;
	isSectionActive: boolean;
	variant?: 'default' | 'inverted';
}) {
	const isResponsive = useMediaQuery(getMediaQuery('xl'));
	const isLargeDesktop = useMediaQuery(getMediaQuery('3xl'));
	const router = useRouter();
	const { t } = useTranslation(['core', 'homepage']);
	if (!subsection) {
		return null;
	}

	return (
		<>
			<Button
				onClick={handleClick}
				className={mergeClasses(
					navLinks,
					'relative flex w-full items-center justify-between py-0 text-gray-900',
					{ 'xl:text-blue-500': isSectionActive },
					{ 'xl:text-white': variant === 'inverted' && !isScrolling }
				)}
				aria-label={sectionName}
				variant="unstyled"
				analyticsParam={`headerSubmenu${uppercaseFirstLetter(sectionId)}`}
				testId={`headerSubmenu${uppercaseFirstLetter(sectionId)}`}
			>
				<h4 className={mergeClasses('mr-0.5 text-base text-[15px] font-normal whitespace-nowrap')}>
					{sectionName}
				</h4>

				<DynamicDirectionIndicators
					isOpen={isOpen}
					className={variant === 'inverted' && !isScrolling ? 'xl:text-white' : undefined}
					variant="onlyArrow"
				/>

				<motion.div
					className="absolute left-6 z-0"
					animate={isOpen ? 'open' : 'closed'}
					initial="closed"
					variants={polygonVariants}
					custom={{ isScrolling, isResponsive, isLargeDesktop } satisfies VariantsOptions}
				>
					<MenuPolygon />
				</motion.div>
			</Button>

			<div
				className={mergeClasses(
					'pointer-events-none fixed top-1/2 left-0 z-1 h-full w-full bg-gray-900 opacity-0 transition-opacity ease-in xl:top-0',
					isOpen && 'opacity-10'
				)}
			/>

			<motion.ul
				className={mergeClasses(
					'fixed z-2 mt-2 max-h-[calc(80vh-1.5rem)] w-full gap-8 overflow-y-auto bg-white px-4 pt-10 pb-10 xl:max-h-[65vh]! xl:rounded-3xl',
					'items-start sm:grid-cols-2 xl:absolute xl:mt-0 xl:mb-6 xl:grid-cols-3 xl:gap-5 xl:bg-white xl:p-10',
					'shadow-floating xl:max-h-none xl:w-auto',
					isOpen ? 'grid' : 'hidden'
				)}
				animate={isOpen ? 'open' : 'closed'}
				variants={variants}
				initial="closed"
				custom={
					{
						isScrolling,
						isResponsive,
						isLargeDesktop,
					} satisfies VariantsOptions
				}
			>
				<li className="absolute top-4 right-4 z-10 xl:hidden">
					<Button
						analyticsParam="close"
						testId="close"
						variant="unstyled"
						type="button"
						onClick={handleClick}
						className={mergeClasses('flex h-7 w-7 text-gray-900')}
					>
						<CrossIcon className="h-full w-full" />
						<span className="sr-only">{t('close')}</span>
					</Button>
				</li>

				{subsection.map((subsection) => {
					const isSubsectionActive = isSectionActive && router.pathname === subsection.path;

					return (
						<motion.li
							key={subsection.id}
							className={mergeClasses(
								'flex items-center transition-colors',
								isHighlighted(subsection.id) && 'mt-3 xl:mt-0'
							)}
							variants={itemVariants}
						>
							<div
								className={mergeClasses(
									'relative flex w-full items-center gap-3 bg-white text-gray-900 no-underline transition',
									'group xl:hover:bg-blue-500 xl:hover:text-white',
									'items-start xl:w-96 xl:rounded-2xl xl:bg-inherit xl:p-4 xl:py-3',
									isSubsectionActive && 'xl:bg-blue-500 xl:text-white'
								)}
							>
								<Link
									to={subsection.path}
									analyticsParam={`headerSubmenu${uppercaseFirstLetter(subsection.id)}`}
									testId={`headerSubmenu${uppercaseFirstLetter(subsection.id)}`}
									className="absolute top-0 right-0 bottom-0 left-0 z-0"
									aria-label={subsection.name}
								/>
								<div
									className={mergeClasses(
										'relative z-10 text-blue-500 group-hover:text-white',
										isSubsectionActive && 'xl:text-white'
									)}
								>
									{subsection.image.src && (
										<div className="flex h-[5.5rem] w-[5.5rem] items-center justify-center rounded-md bg-white xl:h-13 xl:w-13">
											<Image
												src={subsection.image.src}
												alt={subsection.name}
												width={68}
												height={68}
												className="max-h-[4.25rem] max-w-[4.25rem] xl:max-h-10 xl:max-w-10"
											/>
										</div>
									)}
								</div>
								<div>
									<h5 className="text-sm font-semibold xl:mb-2 xl:text-base">{subsection.name}</h5>
									<ul>
										{subsection.items?.map((item) => (
											<li key={item.id}>
												<Link
													to={item.path}
													analyticsParam={`headerSubmenuItem${uppercaseFirstLetter(item.id)}`}
													testId={`headerSubmenuItem${uppercaseFirstLetter(item.id)}`}
													aria-label={item.name}
													className="relative z-10 text-sm text-gray-900 no-underline xl:group-hover:text-white xl:hover:underline"
												>
													{item.name}
												</Link>
											</li>
										))}
										<li>
											<Link
												to={subsection.path}
												analyticsParam={`submenuMore${uppercaseFirstLetter(subsection.id)}`}
												testId={`submenuMore${uppercaseFirstLetter(subsection.id)}`}
												className="relative z-10 text-sm text-gray-900 xl:group-hover:text-white"
											>
												{t('showMore')}
											</Link>
										</li>
									</ul>
								</div>
							</div>
						</motion.li>
					);
				})}
				<motion.li
					className="flex items-center justify-center pt-2 sm:col-span-2 xl:hidden"
					variants={itemVariants}
				>
					<Button
						variant="secondary"
						testId="close"
						analyticsParam="close"
						onClick={handleClick}
						size="sm"
					>
						{t('close')}
					</Button>
				</motion.li>
			</motion.ul>
		</>
	);
}
